// Backend processor enum (from config.js PAYMENT_PROCESSOR_ENUM)
export const PROCESSOR = {
  VALMAR: 1,
  AUTHNET: 2,
  PADDLE: 3,
  BTCPAY: 4,
  WHOP: 5,
  APPLE: 6,
  GOOGLE: 7,
};

export function checkIsIapSubscriber(purchase) {
  if (!purchase || purchase.status !== 'active') return false;
  return purchase.processor === PROCESSOR.APPLE || purchase.processor === PROCESSOR.GOOGLE;
}

export function checkIsWhopSubscriber(purchase, isInternational, hasWhopSubscription = false) {
  // An active Purchase row with a KNOWN processor is authoritative. This keeps
  // a stale denormalized Whop flag (user.whop.status can drift to 'active' when
  // a payment-failed webhook is missed) from overriding a real Apple/Google/
  // Valmar/Paddle/Authnet subscription.
  if (purchase && purchase.status === 'active') {
    if (checkIsIapSubscriber(purchase)) return false;
    if (purchase.processor === PROCESSOR.WHOP) return true;
    if (purchase.processor) return false;
  }
  // No authoritative processor on the Purchase row (missing/stale/legacy row):
  // trust the user's denormalized Whop state (set by webhooks). Without this, a
  // Whop Plus subscriber whose active-purchase didn't load loses cancel/manage.
  if (hasWhopSubscription) return true;
  // Fall back to the legacy "no active purchase" behavior.
  if (!purchase || purchase.status !== 'active') return isInternational;
  return false;
}

/**
 * Known Whop Plus subscribers must stay on Whop for web checkout
 * (credit packs, extend) even when IP geo says US or the lookup fails.
 * Does not treat "international with no purchase" as a Whop subscriber —
 * geo routing handles that separately.
 */
export function shouldForceWhopCheckout(purchase, hasWhopSubscription = false) {
  return checkIsWhopSubscriber(purchase, false, hasWhopSubscription);
}

export function checkIsValmarSubscriber(purchase, { hasValmarPaymentMethod = false, isPlus = false } = {}) {
  if (purchase?.status === 'active' && purchase.processor === PROCESSOR.VALMAR) {
    return true;
  }
  // Plus users with a Valmar card on file but a missing/stale Purchase row
  // (e.g. active-purchase returned null) still bill through Valmar.
  return Boolean(isPlus && hasValmarPaymentMethod);
}

export function checkIsPaddleSubscriber(purchase) {
  if (!purchase || purchase.status !== 'active') return false;
  return purchase.processor === PROCESSOR.PADDLE;
}

export function checkIsGoogleIapSubscriber(purchase) {
  if (!purchase || purchase.status !== 'active') return false;
  return purchase.processor === PROCESSOR.GOOGLE;
}

export function checkIsAppleIapSubscriber(purchase) {
  if (!purchase || purchase.status !== 'active') return false;
  return purchase.processor === PROCESSOR.APPLE;
}

export const LIVE_SUBSCRIPTION_PROCESSORS = [
  PROCESSOR.VALMAR,
  PROCESSOR.WHOP,
  PROCESSOR.APPLE,
  PROCESSOR.GOOGLE,
];

export function isLiveSubscriptionProcessor(processor) {
  return LIVE_SUBSCRIPTION_PROCESSORS.includes(processor);
}

/**
 * Whether Billing / checkout should treat the user as a current Plus member.
 * A past plusExpiresAt means the paid period ended unless a live processor
 * (Valmar/Whop/Apple/Google) still has an active row — webhook lag on renewal
 * day. Retired Paddle/Authnet `active` rows do not keep access.
 */
export function hasValidPlusAccess({
  isPlus = false,
  plusExpiresAt = null,
  isComped = false,
  purchase = null,
} = {}) {
  if (isComped && isPlus) return true;

  const expiry = plusExpiresAt ? new Date(plusExpiresAt) : null;
  const expiryInPast = Boolean(expiry && !Number.isNaN(expiry.getTime()) && expiry <= new Date());
  const liveActivePurchase = purchase?.status === 'active'
    && !purchase?.canceledDate
    && purchase?.recurring?.enabled === true
    && isLiveSubscriptionProcessor(purchase.processor);

  if (expiryInPast) {
    if (liveActivePurchase) return true;
    // A loaded purchase that is not a live recurring sub (retired Paddle/
    // Authnet, canceled, one-time) means the paid period ended. Do not
    // trust a stale persisted isPlus flag.
    if (purchase) return false;
    // No purchase on the client: User.serialize already verified a live
    // sub when it still reports isPlus after expiry (renewal-day lag).
    return !!isPlus;
  }
  if (isPlus) return true;
  return false;
}

export function getPurchaseProductId(purchase) {
  const product = purchase?.product?.id || purchase?.product;
  return typeof product === 'string' ? product : null;
}

export function isPremiumPurchaseProduct(purchase) {
  const product = getPurchaseProductId(purchase);
  return Boolean(product && product.startsWith('plus-premium'));
}

export function isPlusPurchaseProduct(purchase) {
  const product = getPurchaseProductId(purchase);
  return Boolean(product && product.startsWith('plus-') && !product.startsWith('plus-premium'));
}

/**
 * Resolve the billing cadence shown in Billing → Extend.
 * Product SKU is authoritative so legacy Plus annual prices ($100 / $150)
 * are never mistaken for Plus Premium monthly ($99).
 */
export function resolveSubscriptionSchedule(purchase) {
  if (!purchase) return null;
  const product = getPurchaseProductId(purchase);
  const price = Number(purchase.price);

  if (product === 'plus-premium-annual') return 'premium-yearly';
  if (product === 'plus-premium-monthly') return 'premium-monthly';
  if (product && product.startsWith('plus-annual')) return 'yearly';
  if (product?.startsWith('plus-weekly') || purchase.productId?.endsWith?.('.weekly')) return 'weekly';
  if (product?.startsWith('plus-monthly')) return 'monthly';
  if (purchase.recurring?.schedule === 'weekly') return 'weekly';

  // Unknown / missing SKU: price + schedule fallbacks.
  // Premium annual/BTC (~$899–$999) before yearly schedule, otherwise a
  // Premium yearly row with recurring.schedule='yearly' looks like Plus yearly.
  if (Number.isFinite(price) && price >= 850) return 'premium-yearly';
  if (purchase.recurring?.schedule === 'yearly') return 'yearly';
  if (
    purchase.recurring?.schedule === 'monthly'
    && Number.isFinite(price)
    && price >= 90
    && price < 150
  ) {
    return 'premium-monthly';
  }
  if (Number.isFinite(price) && price >= 100 && price < 500) return 'yearly';
  if (Number.isFinite(price) && price >= 90 && price < 150) return 'premium-monthly';
  return 'monthly';
}

/**
 * Hide $20/$200 Plus plans only for actual Plus Premium subscribers.
 * A Plus SKU always keeps Plus extend/renew options visible — a stale
 * isPlusPremium flag or a $99-ish price must not block Plus renewals.
 */
export function shouldHidePlusExtendPlans(purchase, isPlusPremiumFlag = false) {
  if (isPlusPurchaseProduct(purchase)) return false;
  if (isPremiumPurchaseProduct(purchase)) return true;
  const schedule = resolveSubscriptionSchedule(purchase);
  if (schedule === 'premium-monthly' || schedule === 'premium-yearly') return true;
  return Boolean(isPlusPremiumFlag);
}
